/**
 * Admin tab for configuring file upload limits. Stateless — receives
 * current `limits` and an `onChange(partial)` callback that the parent
 * wires to `updateUploadLimits` on AdminContext.
 */

import { useState } from 'react';
import type { UploadLimitsConfig } from '@/types/admin.types';
import { FILE_CONSTRAINTS } from '@/utils/constants';

const ALL_EXTENSIONS = FILE_CONSTRAINTS.SUPPORTED_EXTENSIONS;
const SIZE_MIN = 1;
const SIZE_MAX = 500;
const COUNT_MIN = 1;
const COUNT_MAX = 10;

export interface UploadLimitsTabProps {
  limits: UploadLimitsConfig;
  onChange: (partial: Partial<UploadLimitsConfig>) => void;
}

export function UploadLimitsTab({ limits, onChange }: UploadLimitsTabProps) {
  const [lastExtensionError, setLastExtensionError] = useState<string | null>(null);

  const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (Number.isFinite(value) && value >= SIZE_MIN && value <= SIZE_MAX) {
      onChange({ maxFileSizeMB: value });
    }
  };

  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (Number.isFinite(value) && value >= COUNT_MIN && value <= COUNT_MAX) {
      onChange({ maxFiles: value });
    }
  };

  const toggleExtension = (ext: string) => {
    const isOn = limits.allowedExtensions.includes(ext);
    if (isOn && limits.allowedExtensions.length === 1) {
      setLastExtensionError('At least one file type must remain enabled.');
      return;
    }
    setLastExtensionError(null);
    const next = isOn
      ? limits.allowedExtensions.filter((e) => e !== ext)
      : [...limits.allowedExtensions, ext];
    onChange({ allowedExtensions: next });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary">File Uploads</h3>
        <p className="text-sm text-text-secondary">
          Control file attachment limits across all users.
        </p>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="upload-max-size"
          className="block text-sm font-medium text-text-primary"
        >
          Max file size (MB)
        </label>
        <input
          id="upload-max-size"
          type="number"
          min={SIZE_MIN}
          max={SIZE_MAX}
          value={limits.maxFileSizeMB}
          onChange={handleSizeChange}
          className="w-32 rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
        />
        <p className="text-xs text-text-tertiary">
          Files larger than this are rejected at attach time. Range: {SIZE_MIN}–{SIZE_MAX} MB.
        </p>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="upload-max-count"
          className="block text-sm font-medium text-text-primary"
        >
          Max files per message
        </label>
        <input
          id="upload-max-count"
          type="number"
          min={COUNT_MIN}
          max={COUNT_MAX}
          value={limits.maxFiles}
          onChange={handleCountChange}
          className="w-32 rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
        />
        <p className="text-xs text-text-tertiary">
          Range: {COUNT_MIN}–{COUNT_MAX} files.
        </p>
      </div>

      <div className="space-y-2">
        <span className="block text-sm font-medium text-text-primary">
          Allowed file types
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3 md:grid-cols-4">
          {ALL_EXTENSIONS.map((ext) => (
            <label key={ext} className="flex items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={limits.allowedExtensions.includes(ext)}
                onChange={() => toggleExtension(ext)}
                aria-label={ext}
              />
              <span>{ext}</span>
            </label>
          ))}
        </div>
        {lastExtensionError && (
          <p className="text-xs text-error">{lastExtensionError}</p>
        )}
        <p className="text-xs text-text-tertiary">
          Uncheck to block users from uploading that type.
        </p>
      </div>
    </div>
  );
}
